import { DeliveryMethod, StoreOrderState } from '@/buyers/_gen/gql'
import { StoreOrderStep } from '@/dealers/_gen/gql'
import MoneyContext from '@/gf/modules/Money'
import Time from '@/gf/modules/Time'
import { MoneyPayload, PaymentStatus } from '@/types'
import { gql, useQuery } from '@apollo/client'

const doc = gql`
  query ($filters: [[String]]!, $timezone: String!) {
    fetchOrderReportingMetrics(filters: $filters, timezone: $timezone) {
      total {
        amount
        currency
      }
      netTotal {
        amount
        currency
      }
      chartData {
        date
        total {
          amount
          currency
        }
        netTotal {
          amount
          currency
        }
      }
      allStoreOrders {
        total {
          amount
          currency
        }
        netTotal {
          amount
          currency
        }
        storeOrder {
          id
          shortId
          insertedAt
          state
          step
          deliveryMethod
          paymentStatus
        }
        requestForQuote {
          id
          shortId
          insertedAt
          owners {
            id
            displayName
          }
        }
      }
    }
  }
`

type Data = {
  fetchOrderReportingMetrics: {
    total: MoneyPayload
    netTotal: MoneyPayload
    chartData: {
      total: MoneyPayload | null
      netTotal: MoneyPayload | null
      date: string
    }[]
    allStoreOrders: {
      total: MoneyPayload
      netTotal: MoneyPayload
      storeOrder: {
        id: string
        shortId: string
        insertedAt: string
        state: StoreOrderState
        step: StoreOrderStep
        deliveryMethod: DeliveryMethod | null
        paymentStatus: PaymentStatus
      }
      requestForQuote: {
        id: string
        shortId: string
        insertedAt: string
        owners: { id: string; displayName: string }[]
      }
    }[]
  }
}

type Vars = {
  filters: string[][]
  timezone: string
}

type Props = Omit<Vars, 'filters'> & {
  filters?: Vars['filters']
}

const useOrderReportingMetrics = ({ filters = [], timezone }: Props) => {
  const { loading, error, data } = useQuery<Data, Vars>(doc, {
    variables: { filters, timezone },
    fetchPolicy: 'no-cache',
  })

  const reportingMetrics = data && {
    ...data.fetchOrderReportingMetrics,
    total: MoneyContext.fromPayload(data.fetchOrderReportingMetrics.total),
    netTotal: MoneyContext.fromPayload(data.fetchOrderReportingMetrics.netTotal),
    chartData: data.fetchOrderReportingMetrics.chartData.map((chartDatum) => ({
      ...chartDatum,
      total: chartDatum.total ? MoneyContext.fromPayload(chartDatum.total) : null,
      netTotal: chartDatum.netTotal ? MoneyContext.fromPayload(chartDatum.netTotal) : null,
    })),
    allStoreOrders: data.fetchOrderReportingMetrics.allStoreOrders.map((payload) => ({
      ...payload,
      total: MoneyContext.fromPayload(payload.total),
      netTotal: MoneyContext.fromPayload(payload.netTotal),
      storeOrder: {
        ...payload.storeOrder,
        insertedAt: Time.fromPayload(payload.storeOrder.insertedAt),
      },
      requestForQuote: payload.requestForQuote
        ? {
            ...payload.requestForQuote,
            insertedAt: Time.fromPayload(payload.requestForQuote.insertedAt),
          }
        : null,
    })),
  }

  return { loading, error, data: reportingMetrics }
}

export type OrderReportingMetrics = NonNullable<ReturnType<typeof useOrderReportingMetrics>['data']>

useOrderReportingMetrics.Query = doc
export default useOrderReportingMetrics
